import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Box, Center, Loader, Stack, Text, Alert, Anchor } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import { physicianPortalService } from "../../services/physicianPortal.service";

export function PortalMagicAccessPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const navigate = useNavigate();
  const login = usePortalAuthStore((s) => s.login);

  const [error, setError] = useState<string | null>(null);

  const handleExchangeFailure = useCallback((exchangeError: unknown) => {
    const code = axios.isAxiosError(exchangeError)
      ? exchangeError.response?.data?.error
      : undefined;
    if (code === "MAGIC_LINK_USED") {
      setError("This link has already been used.");
      return;
    }
    if (code === "MAGIC_LINK_EXPIRED") {
      setError("This link has expired.");
      return;
    }
    setError("This link is invalid or no longer active.");
  }, []);

  const exchangeMagicLink = useCallback(async () => {
    if (!token) {
      setError("Invalid link — no token found.");
      return;
    }
    try {
      const result = await physicianPortalService.exchangeMagicLink(token);
      login(result.sessionToken, result.email, result.practiceNames);
      const destination = result.caseId
        ? `/portal/report/${result.caseId}`
        : "/portal/dashboard";
      navigate(destination, { replace: true });
    } catch (exchangeError) {
      handleExchangeFailure(exchangeError);
    }
  }, [handleExchangeFailure, login, navigate, token]);

  useEffect(() => {
    void exchangeMagicLink();
  }, [exchangeMagicLink]);

  if (error) {
    return (
      <Box
        style={{
          minHeight: "calc(100vh - 64px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 480 }}>
          <Stack gap="md">
            <Alert
              icon={<IconAlertCircle size={18} />}
              color="red"
              title="Link no longer valid"
              variant="light"
            >
              {error} Please{" "}
              <Anchor
                component={Link}
                to="/portal/login"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                log in
              </Anchor>{" "}
              to access your reports.
            </Alert>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Center h="calc(100vh - 64px)">
      <Stack align="center" gap="sm">
        <Loader color="indigo" />
        <Text
          size="sm"
          c="dimmed"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          Opening your report…
        </Text>
      </Stack>
    </Center>
  );
}

export default PortalMagicAccessPage;
